import { stores } from '@kurtosys/app-start';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext';
import { IQueryOptions } from '@kurtosys/types/query/IQueryOptions';
import { getExtension } from 'mime';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles, IConfiguration, TStoreContext } from '../../../models/index.js';

import { AppStore } from './AppStore.js';
import { KurtosysApiStore } from './KurtosysApiStore.js';

export class DownloadStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	@observable isLoading = false;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);

		makeObservable(this);
	}

	async initialize() {
		// Add init code here
	}

	onDownloadAll = async (ids: string[]) => {
		if (!this.isLoading && ids.length > 0) {
			this.saveDocumentZip(ids, 'documents');
		}
	};

	getDocument = async (clientCode: string) => {
		if (clientCode) {
			const kurtosysApiStore = this.storeContext.get<KurtosysApiStore>('kurtosysApiStore');
			const overrideOptions = {
				disableCachableRequests: true,
				queryString: { clientCode },
			};
			return await kurtosysApiStore.retrieveDocument.execute(overrideOptions);
		}
		return;
	};

	getDocumentZip = async (ids: string[]) => {
		if (ids && ids.length > 0) {
			const kurtosysApiStore = this.storeContext.get<KurtosysApiStore>('kurtosysApiStore');
			const overrideOptions = {
				disableCachableRequests: true,
				body: { documentCodes: ids },
			};
			return await kurtosysApiStore.retrieveDocumentZip.execute(overrideOptions);
		}
		return;
	};

	@action
	saveDocument = async (payload: any, filename: IQueryOptions): Promise<void> => {
		try {
			runInAction(() => {
				this.isLoading = true;
			});
			const result: any = await this.getDocument(payload.clientCode);
			const parsedFilename = this.getQueryValue(filename, payload);

			const savedFilename = this.saveFile(result, parsedFilename);
			this.logAnalytics(result, savedFilename, payload.clientCode);
		} catch (e) {
			console.warn(e);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	@action
	saveDocumentZip = async (ids: string[], filename: string) => {
		try {
			runInAction(() => {
				this.isLoading = true;
			});
			const result: any = await this.getDocumentZip(ids);

			const savedFilename = this.saveFile(result, filename);
			this.logAnalytics(result, savedFilename);
		} catch (e) {
			console.warn(e);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	logAnalytics = (blob: any, filename: string, clientCode?: string) => {
		const extension = getExtension(blob.type);
		const fullFileName = extension ? `${filename}.${extension}` : filename;

		const appStore = this.storeContext.get<AppStore>('appStore');
		const kurtosysApiStore = this.storeContext.get<KurtosysApiStore>('kurtosysApiStore');

		const { serviceUrl, token } = kurtosysApiStore;
		const analyticsHelper = appStore.analyticsHelper;

		// The helper might be needed to be switched off due to the user not giving consent
		if (analyticsHelper) {
			let context: Partial<IAppContext> = {
				filename: fullFileName,
			};
			if (clientCode) {
				context = {
					...context,
					url: `${serviceUrl}/documents/retrieveDocument/?token=${token}&clientCode=${clientCode}&filename=${filename}`,
				};
			}
			analyticsHelper.logEvent({
				context,
				event: 'document_download',
				eventType: 'click',
			});
		}
	};
}
